import { Component, ViewChild, OnInit, TemplateRef, HostListener } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { BsModalService, BsModalRef, ModalDirective, ModalModule } from 'ngx-bootstrap/modal';
import {
  ADMIN,
  CANVAS_DASHBOARD,
  FILE_MANAGER,
  PRINT_FILE,
  PLAN_SELECTION,
  SCHOOL,
  SCHOOL_DASHBOARD,
  USER_HOME,
  CANVAS_DASHBOARD_DRIVE_FILES,
  SIGN_UP,
  NYCDOE_CALLBACK,
  MANAGE_STUDENTS,
  CHOOSE_MATH_LEVEL,
} from 'src/app/shared/constant/routes';
import { FirebaseAuthService } from './features/user/auth/services/firebase-auth.service';
import { isMacOS } from './shared/helper/utils';
import { RequestService, SessionManagerService, ToastService } from './shared/services';
import { Constants } from './shared/constant/constants';
import { AzureAuthService } from './features/user/auth/services/azure-auth.service';
import { environment } from 'src/environments/environment';
import { UpdateNoticeService } from './shared/services/update-notice.service';
import { IosDetectionService } from './shared/services/ios-detection.service';
import { ToolbarService } from './features/student/canvas-dashboard/services/toolbar.service';
import { GooglePlayService } from './shared/services/google-play.service';
import { NgIf } from '@angular/common';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { LANGUAGES } from '@app/shared/constant/available-langs';
import { ThemeService } from './shared/services/theme/theme.service';
import { THEMES, THEME_IDS } from './shared/services/theme/themes';
import { CheckSubscriptionExpiration } from './shared/services/check-subscription-expiration.service';
import { IUserPreferences } from './shared/models/IUserDetails';
import { IosUpdateNoticeComponent } from './shared/components/ios-update-notice/ios-update-notice.component';
import { VoiceControlLanguageService } from './shared/services/voice-control-language.service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterOutlet, NgxUiLoaderModule, ModalModule, NgIf, TranslateModule, IosUpdateNoticeComponent],
})
export class AppComponent implements OnInit {
  title = 'kiwiWrite';
  nofooterroute = [
    CANVAS_DASHBOARD.URL,
    FILE_MANAGER.URL,
    PRINT_FILE.URL,
    ADMIN.URL,
    SCHOOL_DASHBOARD.URL,
    CANVAS_DASHBOARD_DRIVE_FILES.URL,
    SIGN_UP.URL,
    NYCDOE_CALLBACK.URL,
    MANAGE_STUDENTS.URL,
    CHOOSE_MATH_LEVEL.URL,
  ];
  showFooter = false;
  showSubscriptionMessage = false;

  updateModalRef?: BsModalRef;
  @ViewChild('template')
  modalTemplate!: TemplateRef<any>; // This assumes you have a modal template with a template reference variable 'template'
  isForceUpdate = false;
  private isForceReloadInFlight = false;

  // iOS version detection properties
  showIosUpdateNotice = false;
  currentIosVersion = '';
  minSupportedIosVersion = Constants.IOS_SUPPORT.MIN_SUPPORTED_VERSION_STRING;

  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date;
  public modalRef?: BsModalRef;
  envAppVersion = environment.appVersion;

  @ViewChild('childModal', { static: false }) childModal!: ModalDirective;
  constructor(
    private readonly router: Router,
    private readonly firebaseAuth: FirebaseAuthService,
    private sessionManagerService: SessionManagerService,
    private idle: Idle,
    private keepalive: Keepalive,
    private requestService: RequestService,
    private flashMessage: ToastService,
    private modalService: BsModalService,
    private azureservice: AzureAuthService,
    private updateNoticeService: UpdateNoticeService,
    private iosDetectionService: IosDetectionService,
    private toolbarService: ToolbarService,
    private checkSubscriptionExpiration: CheckSubscriptionExpiration,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private googlePlayService: GooglePlayService,
    private voiceControlLanguageService: VoiceControlLanguageService,
  ) {
    const preferenceDetails = this.sessionManagerService.getUserPreferences();
    this.setHighContrastIfSelected(preferenceDetails);

    // * WHEN APP BOOTS UP
    this.translateService.addLangs([LANGUAGES.ENGLISH, LANGUAGES.SPANISH, LANGUAGES.FRENCH]);
    this.translateService.setDefaultLang(LANGUAGES.ENGLISH);
    const persistedLanguage = this.resolveInitialLanguage();
    localStorage.setItem('lang', persistedLanguage);
    this.translateService.use(persistedLanguage);
    document.documentElement.lang = persistedLanguage;

    router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        //save the path, and clear it.

        if (!event.url.includes('/student/dashboard')) {
          const preferenceDetails = this.sessionManagerService.getUserPreferences();
          this.setHighContrastIfSelected(preferenceDetails);
        }
        this.showFooter = !this.nofooterroute.find(item => event.url.toString().includes(item));
        this.showSubscriptionMessage = location.pathname.includes(PLAN_SELECTION.URL);
      }
    });
    if (!location.pathname.includes(ADMIN.URL) && !location.pathname.includes(SCHOOL.URL)) {
      this.firebaseAuth.checkFirebaseAuth();
      this.sessionManagerService.setPriceId('free_trial' as string);
    }
    document.body.classList.add(isMacOS() ? 'mac' : 'other');

    // sets an idle timeout.
    idle.setIdle(Constants.SESSION_TIME.Set_IdleTime);
    // sets a timeout period of 30 seconds. after 1 hours of inactivity, the user will be considered timed out.
    idle.setTimeout(Constants.SESSION_TIME.Set_Timeout);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => {
      this.reset();
      this.hideChildModal();
    });

    idle.onTimeout.subscribe(() => {
      this.hideChildModal();
      this.idleState = 'Timed out!';
      this.timedOut = true;
      this.logout();
    });

    idle.onIdleStart.subscribe(() => {
      this.idleState = "You've gone idle!";
      this.childModal.show();
    });

    idle.onTimeoutWarning.subscribe(countdown => {
      this.idleState = 'You will time out in ' + countdown + ' seconds!';
    });

    keepalive.onPing.subscribe(() => (this.lastPing = new Date()));

    this.sessionManagerService.getSchoolAdminLoggedIn().subscribe(userLoggedIn => {
      if (userLoggedIn) {
        idle.watch();
        this.timedOut = false;
      } else {
        idle.stop();
      }
    });
  }
  ngOnInit() {
    // Check for unsupported iOS version first - this should run before any other initialization
    this.checkIosVersionSupport();

    //this.updateNoticeService.initEnvAppVerison();
    (window as any).translateService = this.translateService;
    this.CheckUpdateNoticeDisplay();
  }
  checkPath(url: string) {
    return this.nofooterroute.find(item => url.includes(item));
  }

  private resolveInitialLanguage(): string {
    const storedLanguage = localStorage.getItem('lang');
    if (storedLanguage) {
      return this.normalizeLanguageCode(storedLanguage);
    }

    const preferenceLanguage = this.sessionManagerService.getUserPreferences()?.language;
    if (preferenceLanguage) {
      return this.normalizeLanguageCode(preferenceLanguage);
    }

    return LANGUAGES.ENGLISH;
  }

  private normalizeLanguageCode(lang: string): string {
    return (lang || LANGUAGES.ENGLISH).trim().replace('_', '-').toLowerCase();
  }

  /**
   * Checks if the current iOS version is supported
   * Shows iOS update notice if version is 15 or earlier
   */
  checkIosVersionSupport(): void {
    if (this.iosDetectionService.isUnsupportedIosVersion()) {
      this.currentIosVersion = this.iosDetectionService.getCurrentIosVersion() || 'Unknown';
      this.showIosUpdateNotice = true;

      // Prevent further app initialization if iOS version is unsupported
      return;
    }
  }

  reset() {
    this.idle.watch();
    this.timedOut = false;
  }

  hideChildModal() {
    this.childModal.hide();
  }

  logout() {
    this.hideChildModal();
    this.requestService.patch(Constants.SCHOOL_ENDPOINTS.logout).subscribe(
      () => {
        this.sessionManagerService.setSchoolAdminLoggedIn(false);
        this.sessionManagerService.logout();
        this.azureservice.logOutfromAzure();
        this.router.navigate([USER_HOME.URL]);
      },
      error => {
        this.flashMessage.showError(error.error.errorMessage);
      },
    );
  }

  @HostListener('document:visibilitychange', ['$event'])
  onVisibilityChange(): void {
    this.CheckUpdateNoticeDisplay();
  }

  CheckUpdateNoticeDisplay() {
    const isToChecktheVersionAPi = this.updateNoticeService.isVersionDueForCheck();
    const localAppVersion: any = localStorage.getItem('APP_VERSION');
    if (isToChecktheVersionAPi) {
      this.updateNoticeService.apiVersionCallCheck().subscribe(
        res => {
          const data = res.data;
          if (!data?.AppVersion) return;
          if (this.envAppVersion != data.AppVersion) {
            this.isForceUpdate = data.forceUpdate;
            this.checkToDisplayUpdateNotice();
          } else if (localAppVersion != this.envAppVersion) {
            this.isForceUpdate = data.forceUpdate;
            this.checkToDisplayUpdateNotice();
          }
          this.updateNoticeService.updateLocalAppVersion(data.AppVersion, data.updateCheckIntervalMinutes ?? 0, data.updateNoticeCronHour ?? 5);
        },
        err => {
          console.log('err', err);
        },
      );
    }
  }

  checkToDisplayUpdateNotice() {
    const logoutValue = localStorage.getItem('logout');
    if (logoutValue !== null && logoutValue === 'false') {
      location.href = location.href + '?nocache=' + new Date().getTime();
      window.location.reload();
    } else {
      this.displayUpdateNoticeModel();
    }
  }

  displayUpdateNoticeModel() {
    setTimeout(() => {
      const config = {
        ignoreBackdropClick: true,
        class: 'modal-dialog-centered',
      };
      this.updateModalRef = this.modalService.show(this.modalTemplate, config);
    }, 2000);
  }

  async updateNoticeReloadOnClick(userClickEvent: string) {
    if (userClickEvent == 'Reload') {
      if (this.isForceReloadInFlight) return;
      this.isForceReloadInFlight = true;
      try {
        const userRole = this.sessionManagerService.getRole();
        if (userRole != null && userRole == '1') {
          await this.clearCookiesSessionOnForceUpdate();
        }
      } catch (err) {
        console.error('[updateNotice] session teardown failed, reloading anyway:', err);
      }
      location.href = location.href + '?nocache=' + new Date().getTime();
      window.location.reload();
    } else if (userClickEvent == 'Cancel') {
      this.updateModalRef?.hide();
    }
  }
  async clearCookiesSessionOnForceUpdate(): Promise<void> {
    localStorage.setItem('logout', 'true');
    await Promise.race([this.toolbarService.logout(), new Promise<void>(resolve => setTimeout(resolve, Constants.FORCE_UPDATE_LOGOUT_TIMEOUT_MS))]);
    this.sessionManagerService.logout();
  }

  setHighContrastIfSelected(
    preferenceDetails: IUserPreferences & {
      userId?: string;
      username?: string;
      userRole?: '1' | '2' | '3' | '4';
    },
  ) {
    if (preferenceDetails && preferenceDetails.theme && Object.values(THEME_IDS).includes(preferenceDetails.theme)) {
      const theme = THEMES.find(color => color.id === preferenceDetails.theme);
      this.themeService.setColor(theme! || THEMES[0]);
    } else {
      if (preferenceDetails && preferenceDetails.isUseContrastColors) {
        delete preferenceDetails.userId;
        delete preferenceDetails.username;
        this.requestService
          .patch(Constants.ENDPOINTS.userPreferencesSet, {
            ...preferenceDetails,
            userRole: +preferenceDetails.userRole!,
            isUseContrastColors: false,
            theme: THEME_IDS.DEFAULT_HIGHCONTRAST,
          })
          .subscribe(
            (res: any) => {
              const updatedPreferences = res.data;
              this.sessionManagerService.setUserPreferences(updatedPreferences);
              localStorage.setItem(
                'preferences',
                JSON.stringify({
                  ...updatedPreferences,
                }),
              );
              this.themeService.setColor(THEMES[1]!);
            },
            err => {
              this.flashMessage.showError(err.error.errorMessage);
              this.checkSubscriptionExpiration.handleSubscriptionError(err);
            },
          );
      } else {
        // * Use default theme if none is set
        this.themeService.setColor(THEMES[0]);
      }
    }
  }
}
